import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TedChatPanel } from "@/components/TedDrawer";
import { PageIcon } from "@/components/PageIcon";
import { InsightsBoard, InsightDetail, ClassChip } from "@/pages/TedInsights";
import {
  Sparkles, TrendingUp, Truck, Package, Users, PoundSterling, Megaphone, Lightbulb, RefreshCw, ShieldCheck, Clock, ArrowRight, Loader2, ListChecks,
} from "lucide-react";

const SPEC = {
  sales: { label: "Sales", icon: TrendingUp }, operations: { label: "Operations", icon: Truck }, warehouse: { label: "Warehouse", icon: Package },
  customers: { label: "Customers", icon: Users }, finance: { label: "Finance", icon: PoundSterling }, marketing: { label: "Marketing", icon: Megaphone },
  insights: { label: "Ted Insights", icon: Lightbulb }, reports: { label: "Reports", icon: Sparkles }, tasks: { label: "Tasks", icon: ListChecks },
};
const SEV = { high: "bg-rose-100 text-rose-700", medium: "bg-amber-100 text-amber-800", low: "bg-slate-100 text-slate-600" };
const fmt = (v) => { if (!v) return "unknown"; const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); };

function SpecialistRail({ items, active, onPick }) {
  return (
    <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-1 min-w-0" data-testid="ted-cc-specialists">
      <RailButton id={null} label="All areas" icon={Sparkles} count={items.reduce((a, s) => a + s.findings, 0)} active={active === null} onPick={onPick} />
      {items.map((s) => <RailButton key={s.domain} id={s.domain} label={SPEC[s.domain]?.label || s.domain} icon={SPEC[s.domain]?.icon || Sparkles} count={s.findings} active={active === s.domain} onPick={onPick} />)}
    </nav>
  );
}

function RailButton({ id, label, icon: Icon, count, active, onPick }) {
  return (
    <button onClick={() => onPick(id)} data-testid={`ted-cc-specialist-${id || "all"}`}
      className={`shrink-0 flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border hover:bg-slate-50 text-slate-700"}`}>
      <Icon className="w-4 h-4" /> <span className="whitespace-nowrap">{label}</span>
      {count > 0 && <span className={`ml-auto text-[10px] rounded-full px-1.5 ${active ? "bg-white/20" : "bg-rose-100 text-rose-700"}`}>{count}</span>}
    </button>
  );
}

function FindingCard({ f, onAsk, onEvidence, unified }) {
  return (
    <article className="rounded-xl border bg-white p-4 shadow-sm" data-testid={`ted-cc-finding-${f.id}`}>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className={`rounded px-1.5 py-0.5 font-semibold uppercase ${SEV[f.severity] || SEV.low}`}>{f.severity}</span>
        {f.classification && <ClassChip c={f.classification} testid={`ted-cc-class-${f.id}`} />}
        <span className="rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5" data-testid="ted-cc-finding-department">{f.department}</span>
        <span className="rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 inline-flex items-center gap-1" data-testid="ted-cc-finding-quality"><ShieldCheck className="w-3 h-3" />{f.evidence_quality}</span>
        <span className="text-slate-400 inline-flex items-center gap-1" data-testid="ted-cc-finding-freshness"><Clock className="w-3 h-3" />data: {fmt(f.data_freshness)} · read {fmt(f.retrieved_at)}</span>
      </div>
      <h3 className="mt-2 font-semibold text-slate-900">{f.title}</h3>
      <ul className="mt-1 text-sm text-slate-600 list-disc pl-5 space-y-0.5">{f.facts.slice(0, 5).map((x, i) => <li key={i}>{x}</li>)}</ul>
      {!!f.records.length && (
        <div className="mt-2 flex flex-wrap gap-1" data-testid="ted-cc-finding-records">
          {f.records.map((r) => <Link key={`${r.type}:${r.id}`} to={r.link || "#"} title={r.label} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-indigo-100 text-slate-700" data-testid={`ted-cc-record-${r.type}-${r.id}`}>{r.type} {r.id}</Link>)}
        </div>
      )}
      <div className="mt-3 rounded-lg bg-slate-50 p-2 text-xs text-slate-700" data-testid="ted-cc-finding-next-steps">
        <span className="font-semibold">Recommended next step:</span> {f.recommended_next_steps.join("; ")}
        <span className="block text-slate-400 mt-0.5">Ted never changes records — a person makes and approves any change.</span>
      </div>
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onAsk(f.ask_prompt)} data-testid={`ted-cc-ask-${f.id}`}>Ask Ted why <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
        {f.domain === "insights" && (unified
          ? <Button size="sm" variant="ghost" onClick={() => onEvidence(f.id.replace(/^insight:/, ""))} data-testid={`ted-cc-evidence-${f.id}`}>Evidence, history &amp; actions</Button>
          : <Button size="sm" variant="ghost" asChild><Link to="/ted-insights" data-testid={`ted-cc-open-insights-${f.id}`}>Open in Ted Insights</Link></Button>)}
      </div>
    </article>
  );
}

export default function TedCommandCentre() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [active, setActiveRaw] = useState(params.get("area") || null);
  const [evidenceId, setEvidenceId] = useState(null);
  const setActive = (a) => { setActiveRaw(a); setParams(a ? { area: a } : {}, { replace: true }); };
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/ted/briefing").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { api.get("/ted/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false })); }, []);
  useEffect(() => { if (status?.enabled) { load(); } }, [status, load]);
  const ask = (prompt) => {
    window.dispatchEvent(new CustomEvent("ted:prefill", { detail: { prompt } }));
    const el = document.querySelector("[data-testid=ted-cc-chat] [data-testid=ted-input]");
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.bottom > window.innerHeight - 90 || r.top < 0) { el.scrollIntoView({ behavior: "smooth", block: "center" }); }
    }
    setTimeout(() => { el?.focus({ preventScroll: true }); }, 400);
  };
  if (status && !status.enabled) return <div className="p-8 text-sm text-slate-500" data-testid="ted-cc-disabled">Ted is not switched on for this company.</div>;
  const items = (data?.findings || []).filter((f) => !active || f.domain === active);
  return (
    <div className="px-4 lg:px-8 pt-3 lg:pt-4 pb-6 space-y-4" data-testid="ted-command-centre">
      <header className="flex flex-wrap items-center gap-3">
        <PageIcon />
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ted Command Centre</h1>
          <p className="text-sm text-slate-500">Read-only intelligence across the areas you can see · {data ? `updated ${fmt(data.generated_at)}` : "loading"}</p>
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={load} data-testid="ted-cc-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </header>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[180px_minmax(0,1fr)_minmax(400px,42%)]">
        <SpecialistRail items={data?.specialists || []} active={active} onPick={setActive} />
        <section className="space-y-3 min-w-0" data-testid="ted-cc-findings">
          {active === "insights" && status?.unified ? <InsightsBoard compact onAsk={ask} /> : (<>
          <h2 className="text-base md:text-lg font-semibold text-slate-800">Needs attention now</h2>
          {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          {!loading && !items.length && <p className="text-sm text-slate-500 rounded-xl border bg-white p-4" data-testid="ted-cc-empty">Nothing needs attention in {active ? SPEC[active]?.label : "your areas"} right now.</p>}
          {items.map((f) => <FindingCard key={f.id} f={f} onAsk={ask} onEvidence={setEvidenceId} unified={!!status?.unified} />)}
          </>)}
        </section>
        <aside className="min-w-0 rounded-xl border bg-white flex flex-col min-h-[520px] pb-14 lg:sticky lg:top-4 lg:h-[calc(100vh-10rem)] [&_[data-testid=ted-send]]:mr-28 lg:[&_[data-testid=ted-send]]:mr-0" data-testid="ted-cc-chat">
          <div className="px-3 py-2 border-b text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-indigo-600" /> Ask {active ? SPEC[active]?.label : "Ted"}</div>
          {status?.enabled && <TedChatPanel status={status} placeholder={active ? `Ask about ${SPEC[active]?.label}…` : "Ask Ted…"} testid="ted-cc-chat-panel" />}
        </aside>
      </div>
      <Sheet open={!!evidenceId} onOpenChange={(o) => { if (!o) { setEvidenceId(null); } }}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="ted-cc-evidence-drawer">
          <SheetHeader className="p-4 border-b"><SheetTitle className="flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Insight evidence &amp; history</SheetTitle></SheetHeader>
          {evidenceId && <InsightDetail id={evidenceId} onChanged={() => load()} onAsk={(p) => { setEvidenceId(null); ask(p); }} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
